import { useCallback, useMemo } from "react"
import {
  Block,
  Button,
  Divider,
  LabelMedium,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  useBorrowingFee,
  useBorrowInterestRate,
  useUpdateBorrowedAmount,
} from "#/shared/hooks"
import { usePosthogBorrowMoreModalEvents } from "#/shared/hooks/posthog-events"
import { bigIntToHumanReadableFormat, floatToBigInt } from "#/shared/utils"
import { AmountInput } from "#/ui/Input"
import { ComponentStepProps } from "#/ui/Modal"

import { BorrowProgress } from "../components"

export default function UpdateBalance({ onClickContinue }: ComponentStepProps) {
  const [css, theme] = useStyletron()

  const { emitBorrowMoreBalanceConfirmedEvent } =
    usePosthogBorrowMoreModalEvents()

  const {
    additionalAmount,
    onAdditionalAmountChange,
    isAdditionalAmountValid,
  } = useUpdateBorrowedAmount()

  const { interestRateLabel } = useBorrowInterestRate()

  const { calculateBorrowingFee, borrowingFeeRateLabel } = useBorrowingFee()

  const borrowingFeeAmountLabel = useMemo(
    () =>
      bigIntToHumanReadableFormat(
        calculateBorrowingFee(floatToBigInt(additionalAmount || "0")),
      ),
    [additionalAmount, calculateBorrowingFee],
  )

  const onSubmit = useCallback(() => {
    emitBorrowMoreBalanceConfirmedEvent()
    onClickContinue()
  }, [emitBorrowMoreBalanceConfirmedEvent, onClickContinue])

  return (
    <>
      <BorrowProgress step={1} stepsCount={4} />
      <Block
        className={css({
          padding: theme.sizing.scale800,
          paddingTop: 0,
          height: "100%",
          flexDirection: "column",
          display: "flex",
          gap: theme.sizing.scale800,
        })}
      >
        <AmountInput
          label="Increase borrowed amount by"
          token="MUSD"
          placeholder="0 MUSD"
          unitLabel=" MUSD"
          value={additionalAmount}
          onChange={(ev) => onAdditionalAmountChange(ev.target.value)}
          error={additionalAmount ? !isAdditionalAmountValid : false}
          hasBalanceError={isAdditionalAmountValid}
          errorMessage="Amount must be more than 0 MUSD"
          balanceUsd={additionalAmount || "0.00"}
        />
        <Block
          className={css({
            padding: theme.sizing.scale600,
            display: "flex",
            flexDirection: "column",
            gap: theme.sizing.scale300,
            borderRadius: theme.borders.radius400,
            border: "1px solid",
            borderColor: theme.colors.borderOpaque,
            marginTop: "auto",
          })}
        >
          <Block display="flex" justifyContent="space-between">
            <Block>
              <LabelMedium marginBottom={theme.sizing.scale100}>
                Issuance fee{" "}
                <Block as="span" color={theme.colors.contentTertiary}>
                  ({borrowingFeeRateLabel}%)
                </Block>
              </LabelMedium>
              <ParagraphSmall color={theme.colors.contentSecondary}>
                Paid once
              </ParagraphSmall>
            </Block>

            <LabelMedium>{borrowingFeeAmountLabel} MUSD</LabelMedium>
          </Block>
          <Divider $size="cell" />
          <Block display="flex" justifyContent="space-between">
            <Block>
              <LabelMedium marginBottom={theme.sizing.scale100}>
                Interest Rate (APR)
              </LabelMedium>
              <ParagraphSmall color={theme.colors.contentSecondary}>
                Fixed, based on the debt
              </ParagraphSmall>
            </Block>
            <Block
              className={css({
                textAlign: "right",
              })}
            >
              <LabelMedium marginBottom={theme.sizing.scale100}>
                {interestRateLabel}%/yr
              </LabelMedium>
            </Block>
          </Block>
        </Block>
        <Button
          size="large"
          onClick={onSubmit}
          disabled={!additionalAmount || !isAdditionalAmountValid}
        >
          Continue
        </Button>
      </Block>
    </>
  )
}
